<template>
	<div class="print:hidden">

		<Dropdown position="bottom-start" class="" v-if="withSchedulingSegmentsOptions">
			<template #trigger>
				<button type="button" class="btn btn-outline py-2 pl-4 min-h-0 h-auto normal-case border-base-300">
					<template v-if="scheduledOrNotScheduledGoals === 'scheduled-all'">
						{{ allScheduledGoalsLabel? allScheduledGoalsLabel : $t('All goals already covered this school year') }}
					</template>
					<template v-else-if="scheduledOrNotScheduledGoals === 'scheduled-lesson-goals'">
						{{ $t('Only goals scheduled as lesson goals') }}
					</template>
					<template v-else-if="scheduledOrNotScheduledGoals === 'scheduled-theme-goals'">
						{{ $t('Only goals scheduled as week goals') }}
					</template>
					<template v-else-if="scheduledOrNotScheduledGoals === 'not-scheduled' && goalCollections.length > 0">
						{{ $t('All goals still to be covered this school year') }}
					</template>
					<!-- <template v-if="withGoalCollectionOptions && goalCollections.length > 0 && supplementWithGoalCollection">
						, {{  'supplemented with the goals in: {collectionName}' }}
					</template> -->
					<PencilIcon class="w-4 h-4 ml-2"/>
				</button>
			</template>
			<template #content>
				<div class="dd-goal-segmentation">
					<div class="flex flex-col p-4">
						<label>
							<input type="radio" name="scheduledOrNotScheduledGoals" class="radio radio-xs" value="scheduled-all" v-model="scheduledOrNotScheduledGoals" />
							{{ allScheduledGoalsLabel? allScheduledGoalsLabel : $t('All goals already covered this school year') }}
						</label>
						<label class="ml-6">
							<input type="radio" name="scheduledOrNotScheduledGoals" class="radio radio-xs" value="scheduled-lesson-goals" v-model="scheduledOrNotScheduledGoals" />
							{{ $t('Only goals scheduled as lesson goals') }}
						</label>
						<label class="ml-6">
							<input type="radio" name="scheduledOrNotScheduledGoals" class="radio radio-xs" value="scheduled-theme-goals" v-model="scheduledOrNotScheduledGoals" />
							{{ $t('Only goals scheduled as week goals') }}
						</label>
						<template v-if="withGoalCollectionOptions">
							<label :class="[!goalCollections.length ? 'text-base-content-light' : '']" >
								<input type="radio" name="scheduledOrNotScheduledGoals" :disabled="!goalCollections.length" class="radio radio-xs" value="not-scheduled" v-model="scheduledOrNotScheduledGoals" />
								{{ $t('All goals still to be covered this school year') }}
							</label>
							<div v-if="goalCollections.length > 0" class="text-base-content-light italic ml-7 text-sm">
								<span>
									{{ $t('Based on the goals in {collectionName}', { collectionName: '"' + goalCollections[0].name}) + '"' }}
								</span>
							</div>
							<div v-else class="text-base-content-light italic ml-7 text-sm">
								<span>
									({{ $t('This option becomes available once grade-level goals have been created for your class') }})
								</span>
							</div>
						</template>
					</div>
					<!-- BELOW OPTION DISABLED BECAUSE PROBABLY NO USE FOR IT -->
					<!-- <div v-if="goalCollections.length > 0" class="p-4 border-t">
						<label>
							<input type="checkbox" v-model="supplementWithGoalCollection" class="checkbox checkbox-xs" :disabled="scheduledOrNotScheduledGoals === 'not-scheduled'" />
							{{ 'supplemented with the goals in: {collectionName}' }}
						</label>
					</div> -->
				</div>
			</template>
		</Dropdown>

		<div class="@container bg-base">
			<div class="flex items-center gap-3 my-3 w-full">
				<!-- <FunnelIcon class="w-5 h-5 ml-2"/> -->

				<div class="w-full sm:max-w-64">
					<MultiSelect class="w-full"
						:values="mainGroupFilterValues"
						:selectedIds="currentFilter.getParamValues('mainGroups').map(item => item.id)"
						:withFilter="false"
						buttonFieldClasses="input-sm"
						inputFieldClasses="input-sm input-bordered bg-base"
						dropdownClasses="sm:w-[28rem] left-0"
						@itemSelected="addFilterValue('mainGroups', $event)"
						@itemUnselected="removeFilterValue('mainGroups', $event)"
					>
						<span v-if="currentFilter.getParamValues('mainGroups').length" v-text="currentFilter.getParamValues('mainGroups').length" class="badge badge-neutral px-1.5">
						</span>
						{{ $t('Main groups') }}
						<template #item="itemProps">
							<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
								<input type="checkbox" class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5" :checked="itemProps.selected" />
								<div class="overflow-hidden font-semibold">
									{{ itemProps.value.label }}
								</div>
							</div>
						</template>
					</MultiSelect>
				</div>

				<div class="w-full sm:max-w-40">
					<MultiSelect class="w-full"
						:values="tagFilterValues"
						:selectedIds="currentFilter.getParamValues('tags').map(item => item.id)"
						buttonFieldClasses="input-sm"
						inputFieldClasses="input-sm input-bordered bg-base"
						dropdownClasses="sm:w-64"
						@itemSelected="addFilterValue('tags', $event)"
						@itemUnselected="removeFilterValue('tags', $event)"
						:disabled="tagFilterValues.length === 0"
					>
					<span v-if="currentFilter.getParamValues('tags').length" v-text="currentFilter.getParamValues('tags').length" class="badge badge-neutral px-1.5"></span>
					{{ $tc('Tag', 2) }}
						<template #item="itemProps">
							<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
								<input type="checkbox" class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5" :checked="itemProps.selected" />
								<div class="overflow-hidden font-semibold">
									{{ itemProps.value.label }}
								</div>
							</div>
						</template>
					</MultiSelect>
				</div>

				<div v-if="withPupilSelect">
					<MultiSelect
						:values="pupilOptions"
						:selectedIds="currentFilter.getParamValues('pupilIds').map(val => val.id)"
						buttonFieldClasses="input-sm"
						inputFieldClasses="input-sm input-bordered bg-base"
						dropdownClasses="sm:w-80 left-0"
						@itemSelected="addFilterValue('pupilIds', $event)"
						@itemUnselected="removeFilterValue('pupilIds', $event)"
					>
						{{ $t("Select a pupil") }}

						<template #item="itemProps">
							<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
								<input
									type="checkbox"
									class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5"
									:checked="itemProps.selected"
								/>
								<div class="overflow-hidden font-semibold line-clamp-1">
									{{ itemProps.value.label }}
								</div>
							</div>
						</template>
					</MultiSelect>
				</div>

				<form class="flex items-center sm:max-w-40" @submit="addKeyword">
					<input type="text" class="input input-sm w-full pr-10" :placeholder="$t('Search term')" />
					<button type="submit" class="py-1 -ml-9 pl-2 pr-1 border-l">
						<MagnifyingGlassIcon class="w-4 h-4" />
					</button>
				</form>
			</div>
		</div>

		<div v-if="!currentFilter.equals(initialFilter)" class="flex gap-2 items-start">
			<button type="button" class="btn btn-xs btn-ghost bg-base-100" @click="resetFilter">
				{{ $t('Reset') }}
			</button>
			<div id="filter-labels" class="flex flex-wrap items-start gap-2 mt-0.5">
				<Tag v-for="filterVal in currentFilter.getParamValues('mainGroups')"
					:key="filterVal.id"
					:removeable="true"
					class="tag-xs"
					@remove="removeFilterValue('mainGroups', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
				<Tag v-for="filterVal in currentFilter.getParamValues('tags')"
					:key="filterVal.id"
					class="tag-sky tag-with-icon tag-xs"
					:removeable="true"
					@remove="removeFilterValue('tags', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
				<Tag v-if="withPupilSelect" v-for="filterVal in currentFilter.getParamValues('pupilIds')"
					:key="'pupil-' + filterVal.id"
					:removeable="true"
					class="tag-xs mt-2"
					@remove="removeFilterValue('pupilIds', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
				<Tag v-for="filterVal in currentFilter.getParamValues('keywords')"
					:key="filterVal.id"
					class="tag-keyword tag-with-icon tag-xs"
					:removeable="true"
					@remove="removeFilterValue('keywords', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
			</div>
		</div>
	</div>
</template>


<style scoped>
.long-tag span {
	@apply max-w-sm md:max-w-sm overflow-hidden overflow-ellipsis whitespace-nowrap;
}

.dd-goal-segmentation {
	@apply rounded-md bg-base shadow-lg ring-1 ring-neutral ring-opacity-5;
}
	.dd-goal-segmentation label {
		@apply flex items-center gap-2 py-1;
	}
		.dd-goal-segmentation label input {
			@apply mr-1;
		}
</style>


<script lang="ts">


import {useGoalsStore} from "@/stores/Goals.store";
import { usePupilsStore } from '@/stores/Pupils.store';
import { GoalSource } from "@/utils/GoalSource";
import MultiSelect from "@/components/ui/MultiSelect.vue";
import { CheckIcon, FunnelIcon, ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/vue/24/outline';
import Tag from "@/components/ui/Tag.vue";
import {mapStores} from "pinia";
import {GOAL_SCOPES, GoalFilterDef, LESSON_TYPES} from "@/stores/filters/GoalFilterDef";
import {FilterParamValue} from "@/stores/filters/FilterParamValue";
import {useClassroomsStore} from "@/stores/Classrooms.store.ts";
import {useGoalCollectionsStore} from "@/stores/GoalCollections.store.ts";
import Dropdown from '@/components/ui/Dropdown.vue';


export default {

	emits: ['change'],

	props: {
		filter: {
			type: GoalFilterDef,
			required: false,
		},
		initialFilter: {
			type: GoalFilterDef,
			required: true
		},
		withPupilSelect: {
			type: Boolean,
			default: false,
		},
		// Always an array of GoalSources (one entry per curriculum); never a single source.
		goalSources: {
			type: Array as () => GoalSource[],
			default: () => [],
		},
		// whether or not the goal filter is applied to a goal source or just a list of (imported) goals
		appliedToSource: {
			type: Boolean,
			default: false,
		},
		resultCounter: {
			type: Number,
			default: null,
		},
		toggleButtonLabel: {
			type: String,
			default: null,
		},
		withSchedulingSegmentsOptions: {
			type: Boolean,
			default: false,
		},
		withGoalCollectionOptions: {
			type: Boolean,
			default: false,
		},
		allScheduledGoalsLabel: {
			type: String,
			default: null,
		},
	},

	components: {
		MultiSelect,
		Tag,
		CheckIcon, FunnelIcon, ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon, PencilIcon,
		Dropdown
	},

	data() {
		return {
			categoriesLoaded: false,
			currentFilter: this.filter ? this.filter.clone() : this.initialFilter.clone(),
		}
	},

	computed: {

		...mapStores(useGoalsStore, useClassroomsStore, useGoalCollectionsStore, usePupilsStore),

		mainGroupFilterValues() {
			if(!this.goalSources.length) {
				return [];
			}
			const values: any[] = [];
			this.goalSources.forEach((source: GoalSource) => {
				const items = source.getEmptyStructure()?.items;
				if (items) {
					items.forEach(category => {
						values.push(new FilterParamValue(category.path, category.path + ' - ' + category.title));
					});
				}
			});
			return values;
		},
		tagFilterValues(): FilterParamValue[] {
			if(this.appliedToSource) {
				// show all tags found in the source content(s)
				const allTags = new Set<string>();
				this.goalSources.forEach((source: GoalSource) => {
					source.getTags().forEach((tag: string) => allTags.add(tag));
				});
				return [...allTags].sort().map(tag => new FilterParamValue(tag, tag));
			} else {
				// show all tags found in the imported/used goals
				return this.goalsStore.usedTags.map((tag: string) => new FilterParamValue(tag, tag));
			}
		},

		pupilOptions() {
			return this.pupilsStore.items.map((pupil: any) => {
				return new FilterParamValue(
					pupil.id,
					pupil.getFullName()
				);
			});
		},

		toggleLabel() {
			return this.toggleButtonLabel ?? this.$t('Filter');
		},

		supplementWithGoalCollection: {
			get(): boolean {
				if (!this.filter) {
					return false;
				}

				if (this.currentFilter.goalScopes?.includes(GOAL_SCOPES.IN_COLLECTION) && this.goalCollections[0].id) {
					return true;
				} else {
					return false;
				}
			},
			set(value: boolean) {
				if (value) {
					if (!this.currentFilter.goalScopes) {
						this.currentFilter.goalScopes = [];
					}

					if (!this.currentFilter.goalScopes.includes(GOAL_SCOPES.IN_COLLECTION)) {

						this.currentFilter.goalScopes = [ ...this.currentFilter.goalScopes, GOAL_SCOPES.IN_COLLECTION ];
						this.currentFilter.collection = this.goalCollections[0].id;

					}
				} else {
					if (!this.currentFilter.goalScopes) {
						return;
					}

					this.currentFilter.goalScopes = this.currentFilter.goalScopes.filter(scope => scope !== GOAL_SCOPES.IN_COLLECTION);
					this.currentFilter.collection = null;
				}

				this.updateInitialFilter();
				//this.emitChanges();
			}
		},

		scheduledOrNotScheduledGoals: {
			get() {
				if (!this.filter) {
					return;
				}

				if (!this.currentFilter.goalScopes) {
					return 'unknown';
				}

				if (this.currentFilter.goalScopes.includes(GOAL_SCOPES.PLANNED)) {
					if (
						this.currentFilter.lessonTypes.includes(LESSON_TYPES.THEMES) &&
						this.currentFilter.lessonTypes.includes(LESSON_TYPES.ACTIVITIES) &&
						this.currentFilter.lessonTypes.includes(LESSON_TYPES.COLLECTIVE)
					) {
						return 'scheduled-all';
					} else if (
						this.currentFilter.lessonTypes.includes(LESSON_TYPES.ACTIVITIES) &&
						this.currentFilter.lessonTypes.includes(LESSON_TYPES.COLLECTIVE)
					) {
						return 'scheduled-lesson-goals';
					} else if (this.currentFilter.lessonTypes.includes(LESSON_TYPES.THEMES)) {
						return 'scheduled-theme-goals';
					}
				} else if (this.currentFilter.goalScopes.includes(GOAL_SCOPES.UNPLANNED)) {
					return 'not-scheduled';
				}

				return 'unknown';
			},

			set(value: string) {

				if(value === 'not-scheduled') {
					this.supplementWithGoalCollection = false;
				}

				if (!this.currentFilter.goalScopes) {
					this.currentFilter.goalScopes = [];
				}

				const includeCollected = this.currentFilter.goalScopes.includes(GOAL_SCOPES.IN_COLLECTION);

				const defaultScopes = [];
				if (includeCollected) {
					defaultScopes.push(GOAL_SCOPES.IN_COLLECTION);
				}

				switch (value) {
					case 'scheduled-all':
						this.currentFilter.lessonTypes = [LESSON_TYPES.ACTIVITIES, LESSON_TYPES.COLLECTIVE, LESSON_TYPES.THEMES];
						this.currentFilter.goalScopes = [ GOAL_SCOPES.PLANNED, ...defaultScopes ];
						break;

					case 'scheduled-lesson-goals':
						this.currentFilter.lessonTypes = [LESSON_TYPES.ACTIVITIES, LESSON_TYPES.COLLECTIVE];
						this.currentFilter.goalScopes = [ GOAL_SCOPES.PLANNED, ...defaultScopes ];
						break;

					case 'scheduled-theme-goals':
						this.currentFilter.lessonTypes = [LESSON_TYPES.THEMES];
						this.currentFilter.goalScopes = [ GOAL_SCOPES.PLANNED, ...defaultScopes ];
						break;

					case 'not-scheduled':
						this.currentFilter.lessonTypes = []
						this.currentFilter.goalScopes = [GOAL_SCOPES.UNPLANNED, ...defaultScopes ];
						this.currentFilter.collection = this.goalCollections[0].id;
						break;

					default:
						throw new Error('Unknown value for scheduledOrNotScheduledGoals: ' + value);
				}

				this.updateInitialFilter();
				this.emitChanges();
			}
		},

		goalCollections() {
			return this.goalCollectionsStore.fromClassroomId(this.classroomsStore.getCurrentClassroom().id);
		}
	},

	async beforeMount() {
		if(this.withPupilSelect) {
			await this.pupilsStore.loadAllForClassroom();
		}
		this.goalCollectionsStore.loadOnce();
	},

	methods: {

		resetFilter() {
			this.currentFilter = this.initialFilter.clone();
			this.emitChanges();
		},

		/**
		 * In some cases where we don't want the 'reset' button to be visible when changing specific settings,
		 * we update those specific fields in the initial filter.
		 * Clicking 'reset' will then only reset the other fields.
		 */
		updateInitialFilter() {
			const persistingFields = [ 'lessonTypes', 'goalScopes', 'collection' ];
			for (const field of persistingFields) {
				// @ts-ignore
				this.initialFilter[field] = this.currentFilter[field];
			}
		},

		addKeyword(event) {
			event.preventDefault();
			const keyword = event.target[0].value;
			if(!keyword || keyword.length < 3) {
				return;
			}
			event.target[0].value = '';
			this.addFilterValue('keywords', new FilterParamValue(keyword, keyword));
		},

		addFilterValue(paramName: string, paramValue: FilterParamValue) {
			this.currentFilter.addParamValue(paramName, paramValue);
			this.emitChanges();
		},

		removeFilterValue(paramName: string, paramValue: FilterParamValue) {
			this.currentFilter.removeParamValue(paramName, paramValue);
			this.emitChanges();
		},

		emitChanges() {
			this.$emit('change', this.currentFilter);
		}

	}

}
</script>
